/**
 * Shared input mapping for the per-rule "Counter → market (s)" numeric
 * field (`counterStuckMarketHedgeSeconds?: number | null`).
 *
 * If the resting counter maker hasn't filled after N seconds, the engine
 * cancels it and hedges at market. The rule field is nullable: `null`
 * (or absent) means OFF — on the opener it then inherits the group's
 * fallback value (both empty = off). An EMPTY input therefore maps to an
 * explicit `null` (clear), NEVER `0`; a set value must be a positive
 * integer of seconds.
 */

export const COUNTER_STUCK_SECONDS_LABEL = 'Counter → market (s)';

export const COUNTER_STUCK_SECONDS_HELP =
  'Counter → market: if the resting counter maker is unfilled after N seconds, cancel it and cross the book at market price. Empty = off (opener: inherits group default).';

export const COUNTER_WALK_STEP_SECONDS_HELP =
  'Walk step interval (s): every N seconds, reprice the counter maker one step closer to market. Works together with walk step cents. Empty = walk off.';

export const COUNTER_WALK_STEP_CENTS_HELP =
  'Walk step size (¢): move the counter maker price by N cents per step toward market. Works together with walk step interval. Empty = walk off.';

export const COUNTER_STUCK_SECONDS_ERROR = 'Must be a positive integer';

export type ParsedCounterStuckSeconds =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

/** Raw input string → DTO field. Empty = explicit `null` (off / inherit). */
export function parseCounterStuckSecondsInput(
  raw: string
): ParsedCounterStuckSeconds {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n <= 0) {
    return { ok: false, error: COUNTER_STUCK_SECONDS_ERROR };
  }
  return { ok: true, value: n };
}

/** Rule/group field → input string (`null`/absent renders as empty). */
export function counterStuckSecondsToInput(
  value: number | null | undefined
): string {
  return value != null ? String(value) : '';
}
